import { config } from './constants';

export type UserRegistryRecord = {
  id: number;
  userid: string;
  name: string;
};

export type UserRegistryInput = {
  userid: string;
  name: string;
};

export type UserAccessEvent = {
  id: number;
  timestamp: number;
  userRegistryId?: number | null;
  userid: string;
  name?: string;
};

export type PagedResponse<T> = {
  items: T[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
};

export type PushSubscriptionJsonKeys = {
  p256dh: string;
  auth: string;
};

export type PushSubscriptionJson = {
  endpoint: string;
  expirationTime?: number | null;
  keys: PushSubscriptionJsonKeys;
  userAgent?: string;
};

export type PushPublicKeyResponse = {
  publicKey: string;
  enabled: boolean;
};

const baseUrl = config.API_URL;

const readError = async (res: Response): Promise<string> => {
  const text = await res.text();
  return text || `Server error: ${res.status} ${res.statusText}`;
};

const readJson = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    throw new Error(await readError(res));
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
};

export const listUserAccess = (
  page: number,
  size: number,
  userid?: string
): Promise<PagedResponse<UserAccessEvent>> => {
  const query = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  if (userid) {
    query.set('userid', userid);
  }

  return fetch(baseUrl + '/user-access?' + query.toString(), {
    credentials: 'include',
  }).then((res) => readJson<PagedResponse<UserAccessEvent>>(res));
};

export const listUserRegistry = (): Promise<UserRegistryRecord[]> => {
  return fetch(baseUrl + '/user-registry', {
    credentials: 'include',
  }).then((res) => readJson<UserRegistryRecord[]>(res));
};

export const getUserRegistry = (id: number): Promise<UserRegistryRecord> => {
  return fetch(baseUrl + '/user-registry/' + id, {
    credentials: 'include',
  }).then((res) => readJson<UserRegistryRecord>(res));
};

export const createUserRegistry = (input: UserRegistryInput): Promise<UserRegistryRecord> => {
  return fetch(baseUrl + '/user-registry', {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  }).then((res) => readJson<UserRegistryRecord>(res));
};

export const updateUserRegistry = (
  id: number,
  input: UserRegistryInput
): Promise<UserRegistryRecord> => {
  return fetch(baseUrl + '/user-registry/' + id, {
    credentials: 'include',
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  }).then((res) => readJson<UserRegistryRecord>(res));
};

export const deleteUserRegistry = (id: number): Promise<void> => {
  return fetch(baseUrl + '/user-registry/' + id, {
    credentials: 'include',
    method: 'DELETE',
  }).then((res) => readJson<void>(res));
};

export const registerPushSubscription = (subscription: PushSubscriptionJson, userAgent?: string): Promise<void> => {
  return fetch(baseUrl + '/push-subscriptions/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...subscription, userAgent }),
  }).then((res) => readJson<void>(res));
};

export const unregisterPushSubscription = (subscription: PushSubscriptionJson): Promise<void> => {
  return fetch(baseUrl + '/push-subscriptions/unregister', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(subscription),
  }).then((res) => readJson<void>(res));
};

export const getPushPublicKey = (): Promise<PushPublicKeyResponse> => {
  return fetch(baseUrl + '/push-subscriptions/public-key').then((res) => readJson<PushPublicKeyResponse>(res));
};
